import React from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import { useTranslation } from 'react-i18next';
import CustomPagination from './Pagination/Pagination';
import couvertureAbsente from '../../assets/images/couverture-absente.svg';

// ⛔ Une image LOCALE, et non une URL S3. La valeur précédente était une
// URL signée codée en dur, expirée depuis le 11/11/2021 : la vignette de
// repli était donc cassée en production depuis quatre ans, et le lien
// exposait un identifiant de clé dans le bundle livré au navigateur.
const DEFAULT_PICTURE_PLACEHOLDER = couvertureAbsente;

const useStyles = makeStyles()((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
  },
  image: {
    height: '450px',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  cardContent: {
    padding: '0px',
    '&:last-child': {
      paddingBottom: 0,
    },
    height: '100%',
    alignContent: 'bottom',
  },
  card: {
    height: '100%',
    // ⚠️ Un plafond, pas une largeur fixe : dès 1200 px la colonne descend sous
    // 281 px, et `Card` (overflow: hidden) rognait alors couverture et titre.
    width: '100%',
    maxWidth: '281.25px',
    position: 'relative',
  },
  bookDetails: {
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    width: '281.25px',
  },
  featuredContainer: {
    // ⚠️ Un seul `border`, en un seul déclaratif complet (largeur + style +
    // couleur). Ça a longtemps été `borderStyle: 'solid'` PUIS
    // `border: theme.palette.primary.main` : le raccourci `border` fixe aussi
    // le style à sa valeur initiale (`none`) quand il n'est pas précisé, donc
    // la seconde ligne annulait la première — cadre invisible, jamais vu en
    // prod faute d'accès au site.
    border: `1px solid ${theme.palette.primary.main}`,
    // 🔑 Le cadre porte sa PROPRE grille, à la même `spacing` que la grille
    // parente (4). En MUI 5, sa marge négative (-32 px) n'est qu'en haut et à
    // gauche : la gouttière des items l'absorbe, ce `padding` donne l'air
    // intérieur.
    // ⛔ C'était un `<div>` flex à 95 % avec `columnGap` : les colonnes
    // gardaient leurs 25 %, et 4 × 25 % + trois gouttières ne tiennent pas dans
    // 95 %. Les livres à la une passaient donc sur deux lignes AVANT les autres.
    padding: theme.spacing(2),
  },
  archived: {
    opacity: '50%',
    backgroundColor: 'lightgrey',
  },

}));

export default function Collection({
  books, onBookClick, handleSetNewPageNumber, page,
}) {
  const { classes } = useStyles();
  const { t } = useTranslation('myBooks');

  const featuredBooks = books.results.filter((book) => book.featured === true && book.deleted !== true);
  const otherBooks = books.results.filter((book) => book.featured !== true && book.deleted !== true);

  return (
    <>
      {!!featuredBooks.length
            && (
            <Grid item xs={12}>
              <div className={classes.featuredContainer}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    {/* ⚠️ « h2 » : ce sont des SECTIONS de la collection, pas le
                        titre de la page. La vitrine publique, elle, n'a pas d'autre
                        titre — d'où le `component` distinct du `variant`, qui garde
                        la taille sans casser la hiérarchie. */}
                    <Typography variant="h3" component="h2" align="center">
                      {t('add_book.featured')}
                    </Typography>
                  </Grid>
                  {
                            featuredBooks.map((book) => {
                              const cardClasses = `${classes.card} ${book.archived ? classes.archived : 'foo'}`;

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  lg={3}
                                  className={classes.content}
                                  key={book.id}
                                >
                                  <Card className={cardClasses}>
                                    <CardActionArea
                                      className={classes.card}
                                      onClick={(event) => onBookClick(event, book)}
                                    >
                                      <CardHeader
                                        title={book.title}
                                        titleTypographyProps={{ variant: 'h3' }}
                                        subheader={book.archived ? t('add_book.archived') : null}
                                      />
                                      <CardContent className={classes.cardContent}>
                                        <Grid container spacing={2} className={classes.bookDetails}>
                                          <Grid item xs={12}>
                                            <Typography variant="body1" component="p">
                                              {book.author}
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                        <CardMedia
                                          className={classes.image}
                                          image={book.picture || DEFAULT_PICTURE_PLACEHOLDER}
                                          title={book.name}
                                        />

                                      </CardContent>
                                    </CardActionArea>
                                  </Card>
                                </Grid>

                              );
                            })
                        }
                </Grid>
              </div>
            </Grid>
            )}
      <Grid item xs={12}>
        {!!otherBooks.length && (
        <Typography variant="h3" component="h2" align="center">
          {t('collection')}
        </Typography>
        )}
      </Grid>
      {otherBooks.map((book) => {
        const cardClasses = `${classes.card} ${book.archived ? classes.archived : 'foo'}`;

        return (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            className={classes.content}
            key={book.id}
          >
            <Card className={cardClasses}>
              <CardActionArea
                className={classes.card}
                onClick={(event) => onBookClick(event, book)}
              >
                <CardHeader
                  title={book.title}
                  titleTypographyProps={{ variant: 'h3' }}
                  subheader={book.archived ? t('add_book.archived') : null}
                />
                <CardContent className={classes.cardContent}>
                  <Grid container spacing={2} className={classes.bookDetails}>
                    <Grid item xs={12}>
                      <Typography variant="body1" component="p">
                        {book.author}
                      </Typography>
                    </Grid>
                  </Grid>
                  <CardMedia
                    className={classes.image}
                    image={book.picture || DEFAULT_PICTURE_PLACEHOLDER}
                    title={book.name}
                  />

                </CardContent>
              </CardActionArea>
            </Card>

          </Grid>
        );
      })}
      {!!books.results.length
      && (
      <Grid item xs={12}>
        <CustomPagination count={books.num_pages} handleSetNewPageNumber={handleSetNewPageNumber} page={page} />
      </Grid>
      )}
    </>
  );
}
